import React, { useMemo } from "react";
import * as Mui from "@mui/material";
import { KeyboardArrowDown, ChevronRight } from "@mui/icons-material";
import { themes, tableRowSize } from "services/constants";
import { TableEmptyState } from "components/CustomComponents/EmptyState";

// Band label used for the synthetic per-employee rollup (summary) row. Defined
// here and re-used by the report container to avoid a circular import.
export const TOTAL_BAND_LABEL = "Total";

const tableBorderSx = { border: "1px solid #d9d9d9" };

// Stable default for the controlled `expanded` prop so an omitted prop doesn't
// create a new Set on every render.
const EMPTY_EXPANDED = new Set();

// Left-frozen columns. Fixed widths let each pinned column compute its `left`
// offset so it stays put while the day columns scroll horizontally.
const CHEVRON_WIDTH = 44;
const SUMMARY_STICKY_KEYS = new Set(["empId", "empName"]);
const SUMMARY_STICKY_WIDTHS = { empId: 110, empName: 170 };

// Fixed widths for the scrolling (non-frozen) columns. Pinning them keeps every
// day cell the same size and makes the inline band rows line up exactly under
// the day / total headers (no auto-sizing to content).
const DAY_WIDTH = 58;
const TOTAL_HRS_WIDTH = 92;
const COUNT_WIDTH = 96;
const AMOUNT_WIDTH = 110;

// Opaque fills so scrolling day cells slide *under* the frozen columns.
const STICKY_BODY_BG = "#ffffff";
// The employee rollup row is filled like a group header so it stands apart from
// its (white) per-band breakdown rows below it. Kept intentionally light.
const SUMMARY_ROW_BG = "#f5f7fd";
const SUMMARY_ROW_HOVER_BG = "#edf1fb";
// Band rows stay white but carry a coloured left accent + indent so the
// hierarchy (employee ▸ band) reads at a glance.
const BAND_ROW_BG = "#ffffff";
const BAND_ACCENT = "#607eeb";
const BAND_TEXT = "#5a6472";

const formatAmount = (value) => {
  const amount = Number(value) || 0;
  return amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// "Default Band" is the unconfigured band and reads as plain "OT"; configured
// factor bands (1.5, 2, 2.5 ...) are shown exactly as the BE sends them.
export const formatOTBandLabel = (bandName) => {
  if (bandName === TOTAL_BAND_LABEL) {
    return TOTAL_BAND_LABEL;
  }
  return !bandName || bandName === "Default Band" ? "OT" : bandName;
};

// Amount column reads best right-aligned; identity + band columns left;
// everything else (per-day durations + totals) centered.
const alignForKey = (key) => {
  if (key === "earning_amount") {
    return "right";
  }
  if (key === "empId" || key === "empName" || key === "band") {
    return "left";
  }
  return "center";
};

const headSx = {
  backgroundColor: themes.primary,
  position: "sticky",
  top: -1,
  zIndex: 10,
  "& .MuiTableCell-root": {
    backgroundColor: themes.primary,
    color: themes.headLine,
    fontWeight: "bold",
    border: "1px solid #d9d9d9",
    whiteSpace: "nowrap",
  },
};

export const OTReportV2Table = ({
  reportRows,
  employeeIds = [],
  headers = [],
  dateColumns = [],
  // Controlled expand state (a Set of expanded empIds) + per-row toggle, owned
  // by the report page so the toolbar "expand all" button can drive it too.
  expanded = EMPTY_EXPANDED,
  onToggle,
}) => {
  const toggle = (empId) => {
    if (onToggle) {
      onToggle(empId);
    }
  };

  const dateKeys = useMemo(() => {
    const set = new Set();
    dateColumns.forEach((column) => set.add(column.key));
    return set;
  }, [dateColumns]);

  const isDateColumn = (key) => dateKeys.has(key);

  // Summary (employee rollup) row columns: everything except the per-band
  // "Band" column, which is instead shown inline in the Name column of each
  // expanded band row so the day/total columns stay perfectly aligned.
  const summaryHeaders = useMemo(
    () => headers.filter((header) => header.key !== "band"),
    [headers],
  );

  // Fixed-width style for the scrolling columns (day cells + totals). Applied to
  // header, summary and band cells alike so every box is the same size and the
  // columns line up between the rollup row and its expanded band rows.
  const widthSx = (key) => {
    const width = isDateColumn(key)
      ? DAY_WIDTH
      : {
          totalHrs: TOTAL_HRS_WIDTH,
          count: COUNT_WIDTH,
          earning_amount: AMOUNT_WIDTH,
        }[key];
    return width ? { minWidth: width, width, maxWidth: width } : {};
  };

  // Left offset for each frozen summary column = chevron width + the widths of
  // the frozen columns that precede it in the current header order.
  const summaryStickyLeft = useMemo(() => {
    const map = {};
    let offset = CHEVRON_WIDTH;
    summaryHeaders.forEach((header) => {
      if (SUMMARY_STICKY_KEYS.has(header.key)) {
        map[header.key] = offset;
        offset += SUMMARY_STICKY_WIDTHS[header.key] || 120;
      }
    });
    return map;
  }, [summaryHeaders]);

  // Sticky-position styles for a frozen summary column. `isHeader` cells sit
  // above the frozen body cells at the top-left corner.
  const summaryStickySx = (key, isHeader, bodyBg = STICKY_BODY_BG) => {
    if (!SUMMARY_STICKY_KEYS.has(key)) {
      return {};
    }
    const width = SUMMARY_STICKY_WIDTHS[key] || 120;
    return {
      position: "sticky",
      left: summaryStickyLeft[key],
      minWidth: width,
      width,
      maxWidth: width,
      zIndex: isHeader ? 12 : 3,
      backgroundColor: isHeader ? themes.primary : bodyBg,
    };
  };

  // The leading chevron column is always frozen at the very left edge.
  const chevronStickySx = (isHeader, bodyBg = STICKY_BODY_BG) => ({
    position: "sticky",
    left: 0,
    minWidth: CHEVRON_WIDTH,
    width: CHEVRON_WIDTH,
    maxWidth: CHEVRON_WIDTH,
    zIndex: isHeader ? 12 : 3,
    backgroundColor: isHeader ? themes.primary : bodyBg,
  });

  // Rollup row (isTotal) and band rows, grouped per employee, from whatever the
  // toolbar currently hands back (search / filter / sort already applied).
  const summaryByEmp = useMemo(() => {
    const map = new Map();
    reportRows.forEach((row) => {
      if (row.isTotal && !map.has(row.empId)) {
        map.set(row.empId, row);
      }
    });
    return map;
  }, [reportRows]);

  const bandRowsByEmp = useMemo(() => {
    const map = new Map();
    reportRows.forEach((row) => {
      if (row.isTotal) {
        return;
      }
      if (!map.has(row.empId)) {
        map.set(row.empId, []);
      }
      map.get(row.empId).push(row);
    });
    return map;
  }, [reportRows]);

  const renderCellValue = (header, row) => {
    switch (header.key) {
      case "empId":
        return row.empId || "-";
      case "empName":
        return row.empName || "-";
      case "band":
        return formatOTBandLabel(row.band);
      case "totalHrs":
        return row.totalHrs || "";
      case "count":
        return row.count ? row.count.toString() : "";
      case "earning_amount":
        return formatAmount(row.earning_amount);
      default:
        if (isDateColumn(header.key)) {
          // OT duration for the day, already formatted as "HH:MM".
          return row[header.key] || "";
        }
        return "";
    }
  };

  // Cell content for an expanded per-band row. It reuses the summary columns so
  // the day/total values line up under the same headers; the band name takes
  // the place of the employee name and the Emp Id cell is left blank.
  const renderBandCellValue = (header, row) => {
    if (header.key === "empId") {
      return "";
    }
    if (header.key === "empName") {
      return formatOTBandLabel(row.band);
    }
    return renderCellValue(header, row);
  };

  const summaryColumnCount = 1 + Math.max(summaryHeaders.length, 1);

  return (
    <Mui.TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
      <Mui.Table className="border" stickyHeader size="small">
        <Mui.TableHead sx={headSx}>
          <Mui.TableRow sx={tableRowSize}>
            <Mui.TableCell sx={chevronStickySx(true)} />
            {summaryHeaders.map((header) => (
              <Mui.TableCell
                key={header.key}
                align={alignForKey(header.key)}
                sx={{
                  ...widthSx(header.key),
                  ...summaryStickySx(header.key, true),
                }}
              >
                {header.name}
              </Mui.TableCell>
            ))}
          </Mui.TableRow>
        </Mui.TableHead>
        <Mui.TableBody>
          {employeeIds.length === 0 ? (
            <TableEmptyState
              colSpan={summaryColumnCount}
              dataLength={employeeIds}
              title="No Overtime Records Found"
              subtitle="No overtime records for the selected date range."
            />
          ) : (
            employeeIds.map((empId) => {
              const summaryRow = summaryByEmp.get(empId);
              if (!summaryRow) {
                return null;
              }
              const bandRows = bandRowsByEmp.get(empId) || [];
              const isExpanded = expanded.has(empId);
              return (
                <React.Fragment key={empId}>
                  <Mui.TableRow
                    sx={{
                      ...tableRowSize,
                      cursor: "pointer",
                      // Whole employee (group-header) row is tinted; hovering
                      // deepens the tint on every cell, sticky ones included.
                      "& > .MuiTableCell-root": {
                        backgroundColor: SUMMARY_ROW_BG,
                      },
                      "&:hover > .MuiTableCell-root": {
                        backgroundColor: SUMMARY_ROW_HOVER_BG,
                      },
                    }}
                    onClick={() => toggle(empId)}
                  >
                    <Mui.TableCell
                      sx={{
                        ...tableBorderSx,
                        ...chevronStickySx(false, SUMMARY_ROW_BG),
                      }}
                    >
                      <Mui.IconButton
                        size="small"
                        style={{ padding: 0 }}
                        onClick={(event) => {
                          event.stopPropagation();
                          toggle(empId);
                        }}
                        aria-label={isExpanded ? "Collapse" : "Expand"}
                      >
                        {isExpanded ? <KeyboardArrowDown /> : <ChevronRight />}
                      </Mui.IconButton>
                    </Mui.TableCell>
                    {summaryHeaders.map((header) => (
                      <Mui.TableCell
                        key={header.key}
                        align={alignForKey(header.key)}
                        sx={{
                          ...tableBorderSx,
                          color: themes.headLine,
                          ...widthSx(header.key),
                          // Employee identity + totals read bolder than the
                          // per-day cells on the group-header row.
                          fontWeight:
                            header.key === "empId" ||
                            header.key === "empName" ||
                            header.key === "totalHrs" ||
                            header.key === "count" ||
                            header.key === "earning_amount"
                              ? 700
                              : 500,
                          ...summaryStickySx(header.key, false, SUMMARY_ROW_BG),
                        }}
                      >
                        {renderCellValue(header, summaryRow)}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>

                  {isExpanded && bandRows.length === 0 ? (
                    <Mui.TableRow sx={tableRowSize}>
                      <Mui.TableCell
                        sx={{
                          ...tableBorderSx,
                          ...chevronStickySx(false, BAND_ROW_BG),
                        }}
                      />
                      <Mui.TableCell
                        colSpan={Math.max(summaryHeaders.length, 1)}
                        align="center"
                        sx={{
                          ...tableBorderSx,
                          color: BAND_TEXT,
                          backgroundColor: BAND_ROW_BG,
                        }}
                      >
                        No OT band breakdown for this employee.
                      </Mui.TableCell>
                    </Mui.TableRow>
                  ) : null}

                  {isExpanded &&
                    bandRows.map((row) => (
                      <Mui.TableRow
                        key={row.rowId}
                        sx={{
                          ...tableRowSize,
                          "&:hover > .MuiTableCell-root": {
                            backgroundColor: "#f5f7fc",
                          },
                        }}
                      >
                        <Mui.TableCell
                          sx={{
                            ...tableBorderSx,
                            ...chevronStickySx(false, BAND_ROW_BG),
                          }}
                        />
                        {summaryHeaders.map((header) => {
                          const isName = header.key === "empName";
                          const isSticky = SUMMARY_STICKY_KEYS.has(header.key);
                          return (
                            <Mui.TableCell
                              key={header.key}
                              align={alignForKey(header.key)}
                              sx={{
                                ...tableBorderSx,
                                color: BAND_TEXT,
                                ...widthSx(header.key),
                                ...summaryStickySx(
                                  header.key,
                                  false,
                                  BAND_ROW_BG,
                                ),
                                ...(isSticky
                                  ? {}
                                  : { backgroundColor: BAND_ROW_BG }),
                                // The band name sits under the Name column,
                                // indented with a coloured accent bar so the
                                // employee ▸ band nesting is obvious.
                                ...(isName
                                  ? {
                                      pl: 3,
                                      fontWeight: 600,
                                      color: themes.headLine,
                                      borderLeft: `3px solid ${BAND_ACCENT}`,
                                    }
                                  : {}),
                              }}
                            >
                              {renderBandCellValue(header, row)}
                            </Mui.TableCell>
                          );
                        })}
                      </Mui.TableRow>
                    ))}
                </React.Fragment>
              );
            })
          )}
        </Mui.TableBody>
      </Mui.Table>
    </Mui.TableContainer>
  );
};
